// src/pages/AddBankAccount.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useState, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { apiPost, apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type Props = {
  onSuccess?: () => void;
};

interface Account {
  id: number;
  name: string;
  kind: "cash" | "bank";
  initial_balance: number;
}

export default function AddBankAccount({ onSuccess }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<"bank" | "cash">("bank");
  const [initialBalance, setInitialBalance] = useState("");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<"duplicateName" | "save" | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!company_id) return;
    apiGet(`/accounts?company_id=${company_id}`).then((data) => {
      setAccounts(Array.isArray(data) ? data : []);
    });
  }, [company_id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (
      accounts.some((acc) => acc.name.trim().toLowerCase() === trimmed.toLowerCase())
    ) {
      setError("duplicateName");
      return;
    }

    setSaving(true);
    try {
      await apiPost("/accounts", {
        name: trimmed,
        type: "asset",
        kind,
        initial_balance: Number(initialBalance) || 0,
        company_id,
      });

      if (onSuccess) onSuccess();
      else navigate(companyRoute(company_id, "/settings/accounts"));
    } catch (err: any) {
      if (
        err?.response?.status === 400 ||
        (err?.message && String(err.message).includes("UNIQUE constraint"))
      ) {
        setError("duplicateName");
      } else {
        setError("save");
      }
    } finally {
      setSaving(false);
    }
  };

  // AppleClasses
  const labelCls = `block mb-1 ${A.label}`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";
  // ✅ locked header spacing tokens: px-2 + mb-2
  const headerInner =
    "w-full max-w-md mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
            type="button"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h2 className={`flex-1 text-center ${A.title}`}>{t("bankAccounts.addTitle")}</h2>

          <button
            onClick={() => navigate(companyRoute(company_id, "/"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={handleSubmit}
        className={`${A.card} w-full max-w-md mx-auto px-2 sm:px-6`}
      >
        <div className="p-5 md:p-6">
          <div className="mb-5">
            <label className={labelCls}>{t("directory.products.type")}</label>
            <select
              className={`${A.inputBase} appearance-none`}
              value={kind}
              onChange={(e) => setKind(e.target.value as "bank" | "cash")}
            >
              <option value="bank">{t("balanceSheet.bank")}</option>
              <option value="cash">{t("balanceSheet.cash")}</option>
            </select>
          </div>

          <div className="mb-5">
            <label className={labelCls}>{t("bankAccounts.accountName")}</label>
            <input
              className={A.inputBase}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="mb-6">
            <label className={labelCls}>{t("bankAccounts.initialBalance")}</label>
            <input
              type="number"
              step="0.01"
              className={A.inputBase}
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              min="0"
              inputMode="decimal"
              pattern="^\\d*\\.?\\d*$"
              placeholder="0.00"
            />
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error === "duplicateName" ? t("bankAccounts.errors.duplicateName") : t("bankAccounts.errors.save")}
            </div>
          )}

          <Button
            type="submit"
            disabled={saving}
            variant="primary"
            minWidth="100%"
            className="w-full justify-center"
          >
            {saving ? t("directory.common.savingDots") : t("bankAccounts.saveAccount")}
          </Button>
        </div>
      </form>
    </div>
  );
}
